/**
 * @file sideDomain.ts
 * @description GCOME_AP 侧灯领域描述。
 *
 * GCOME_AP 侧灯配置存放在功能变量区（GetFunc/SetFunc）偏移 24-31：
 *   偏移 24 — 侧灯模式（网页版独立编码：0=流光, 1=七彩循环, 2=呼吸, 3=常亮, 4=关闭）
 *   偏移 25 — 侧灯亮度
 *   偏移 26 — 侧灯速度
 *   偏移 27 — 侧灯彩色标志
 *   偏移 28 — 侧灯颜色索引
 *   偏移 29-31 — Side RGB
 * 本领域 parser 以"偏移 24 起"的 8 字节子数组作为 buffer。
 */

import {
  LightingDomainDescriptor,
  LightingDomainType,
  LightingFeatureFlag,
  LightingConfigData,
} from '../../../../types/lightingDomain'

/** 侧灯配置在功能变量区的起始偏移。 */
const SIDE_BASE = 24

/** 侧灯配置占用字节数（偏移 24-31）。 */
const SIDE_SIZE = 8

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value))

/**
 * 侧灯领域 GCOME_AP 独有的扩展字段（侧灯模式/侧灯颜色索引）。
 * 注意 sideMode 必须被 parse 读回并在 serialize 写回 buffer[0]，
 * 否则 shared 的读-改-写（setConfig/setProperty）会把侧灯模式覆盖成 0（流光）。
 */
export type SideConfigExtra = {
  sideMode?: number
  sideColorIndex?: number
}

const sideParser: LightingDomainDescriptor['parser'] = {
  parse: (buffer, index) =>
    ({
      index,
      domainType: LightingDomainType.Side,
      sideMode: buffer[0],
      // 协议亮度本身为 0-100，原样透出（UI 侧按 0-100 步进 25 提供 5 档）
      brightness: buffer[1],
      // GCOME_AP 速度档位语义与直觉相反（协议 0=最快，4=最慢），解析时反转对齐 UI“档位越大越快”
      speed: 4 - buffer[2],
      isColorful: buffer[3] === 1,
      sideColorIndex: buffer[4],
      rgb: { r: buffer[5], g: buffer[6], b: buffer[7] },
      buffer,
    }) as LightingConfigData & SideConfigExtra,
  serialize: (config) => {
    const buf = new Uint8Array(SIDE_SIZE)
    const extra = config as unknown as SideConfigExtra
    buf[0] = clamp(extra.sideMode ?? 0, 0, 0xff)
    // 协议亮度 0-100 原样写入（UI 端滑块按 step=25 量化为 0/25/50/75/100）
    buf[1] = clamp(config.brightness ?? 60, 0, 100)
    // 速度档位反转：UI 4(快) → 协议 0(快)；UI 0(慢) → 协议 4(慢)
    buf[2] = 4 - clamp(config.speed ?? 0, 0, 4)
    buf[3] = config.isColorful ? 1 : 0
    buf[4] = extra.sideColorIndex ?? 0
    buf[5] = config.rgb?.r ?? 0
    buf[6] = config.rgb?.g ?? 0
    buf[7] = config.rgb?.b ?? 0
    return buf
  },
}

const sideAddressResolver: LightingDomainDescriptor['addressResolver'] = {
  getBaseAddr: () => [SIDE_BASE],
  getFieldAddr: (field) => {
    const offsetMap: Record<string, number> = {
      brightness: 1,
      speed: 2,
      isColorful: 3,
      sideColorIndex: 4,
      r: 5,
      g: 6,
      b: 7,
    }
    return [SIDE_BASE + (offsetMap[field] ?? 0)]
  },
}

/** GCOME_AP 侧灯领域。 */
export const sideDomain: LightingDomainDescriptor = {
  type: LightingDomainType.Side,
  configSize: SIDE_SIZE,
  supportedFeatures:
    LightingFeatureFlag.Brightness |
    LightingFeatureFlag.Speed |
    LightingFeatureFlag.SingleColor |
    LightingFeatureFlag.Colorful,
  addressResolver: sideAddressResolver,
  parser: sideParser,
  cacheTags: (_index, configIndex) => [`light:side`, `cfg:${configIndex}`],
}
